/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type React from 'react';
import { useEffect, useState } from 'react';
import { ImageOff } from 'lucide-react';
import { Link } from '@/components/link';
import { useAnalytics } from '@/hooks/use-analytics';
import { resolveImageUrl } from '@/lib/images/resolve-image-url';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/lib/currency';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface Suggestion {
    name: string;
    link: string;
    productId?: string;
    image?: string;
    price?: number;
}

interface SearchSuggestionsPopupProps {
    suggestions?: Suggestion[];
    searchPhrase?: string;
    closeAndNavigate?: (link: string) => void;
    /** True while product images are being resolved via getProducts. */
    hydratingProductImages?: boolean;
}

function SuggestionImagePlaceholder() {
    const { t } = useTranslation('common');

    return (
        <div className="absolute inset-0 flex h-full w-full items-center justify-center bg-muted text-header-foreground">
            <div className="text-center">
                <ImageOff className="mx-auto mb-1 h-8 w-8" />
                <div className="text-xs">{t('noImageAvailable')}</div>
            </div>
        </div>
    );
}

function SuggestionProductImage({
    imageUrl,
    alt,
    productId,
    hydratingProductImages,
}: {
    imageUrl: string;
    alt: string;
    productId?: string;
    hydratingProductImages: boolean;
}) {
    const [loaded, setLoaded] = useState(false);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setLoaded(false);
        setFailed(false);
    }, [imageUrl]);

    const awaitingHydration = !imageUrl && Boolean(productId) && hydratingProductImages;
    const showSkeleton = awaitingHydration || (Boolean(imageUrl) && !loaded && !failed);

    if (!imageUrl && !awaitingHydration) {
        return <SuggestionImagePlaceholder />;
    }

    return (
        <>
            {showSkeleton ? <Skeleton className="absolute inset-0 h-full w-full rounded-none" aria-hidden /> : null}
            {imageUrl ? (
                <img
                    src={imageUrl}
                    alt={alt}
                    className={cn(
                        'absolute inset-0 h-full w-full object-cover transition-opacity',
                        loaded ? 'opacity-100' : 'opacity-0'
                    )}
                    loading="eager"
                    onLoad={() => setLoaded(true)}
                    onError={() => setFailed(true)}
                />
            ) : null}
            {failed ? <SuggestionImagePlaceholder /> : null}
        </>
    );
}

const SearchSuggestionsPopup: React.FC<SearchSuggestionsPopupProps> = ({
    suggestions,
    searchPhrase,
    closeAndNavigate,
    hydratingProductImages = false,
}) => {
    const analytics = useAnalytics();
    const config = useConfig();
    const { t, i18n } = useTranslation('common');
    const { currency } = useSite();
    if (!suggestions || suggestions.length === 0) {
        return null;
    }

    const handleClick = (suggestion: Suggestion) => {
        void analytics.trackClickSearchSuggestion({
            searchInputText: searchPhrase || '',
            suggestion: suggestion.name,
        });
        if (closeAndNavigate) {
            closeAndNavigate(suggestion.link);
        }
    };

    return (
        <div data-testid="sf-horizontal-product-suggestions" className="overflow-hidden">
            <div className="grid grid-cols-2 gap-x-1 gap-y-6">
                {suggestions.map((suggestion) => {
                    const imageUrl = suggestion.image
                        ? resolveImageUrl({ src: suggestion.image, config, context: 'search' })
                        : '';

                    return (
                        <Link
                            data-testid="product-tile"
                            to={suggestion.link}
                            key={suggestion.link}
                            onMouseDown={() => handleClick(suggestion)}
                            className="block min-w-0">
                            <div className="w-full">
                                <div>
                                    <div className="relative aspect-[4/3] w-full overflow-hidden bg-secondary/20">
                                        <SuggestionProductImage
                                            imageUrl={imageUrl}
                                            alt={suggestion.name || t('productImageAlt')}
                                            productId={suggestion.productId}
                                            hydratingProductImages={hydratingProductImages}
                                        />
                                    </div>
                                </div>

                                <div className="flex flex-col gap-0.5 p-3">
                                    <p className="line-clamp-2 text-base font-normal text-header-foreground">
                                        {suggestion.name}
                                    </p>

                                    {suggestion.price ? (
                                        <p className="text-base font-normal text-header-foreground">
                                            {formatCurrency(suggestion.price, i18n.language, currency)}
                                        </p>
                                    ) : null}
                                </div>
                            </div>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
};

export default SearchSuggestionsPopup;
